import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { LeadsApiClient } from '@/utils/api/leadsApi'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { LeadDto } from 'api/Clients/Types/LeadDto'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getLeadFields } from 'components/Filter/getLeadFields'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridState } from 'features/DataGrid'
import uniqBy from 'lodash/uniqBy'
import { useAppDispatch, useAppSelector } from 'store'
import { FilterDescriptor, GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import {
  fetchCountriesAction,
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
} from 'store/identity/identity.actions'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, instanceOfFilterDescriptor } from 'utils/QueryHelpers'

import { leadsColumns } from './columns'
import { normalizeLeadsSearchQuery } from './normalizer'

const useMapStateToProps = () => ({
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  filters: useAppSelector((state) => state.identity.filter),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
  }
}

export type SelectLeadEvent = { filterId?: string; value: LeadDto[] }

interface SelectLeadTableProps {
  filterId?: string
  singleLead?: boolean
  onSelect?: (leads: SelectLeadEvent) => void
  onSelectSingle?: (lead: LeadDto) => void
  onClose: () => void
}

const SelectLeadTable = (props: SelectLeadTableProps): JSX.Element => {
  const [isLoading, setLoading] = useState<boolean>(true)
  const [leads, setLeads] = useState<LeadDto[]>([])
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const [selectedLeads, setSelectedLeads] = useState<LeadDto[]>([])
  const leadsApiClient = useApiClient(LeadsApiClient)

  const { gridState } = useGridState()
  const { filters, tickmillCompanies } = useMapStateToProps()
  const { fetchFilters, fetchTickmillCompanies } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterActionState: GridFilterActionState): Promise<PageData<LeadDto[]>> => {
          const response = await leadsApiClient.getLeads({
            pageIndex: gridFilterActionState.pageIndex,
            pageSize: gridFilterActionState.pageSize,
          })

          if (isEmpty(filters)) await fetchFilters()
          if (isEmpty(tickmillCompanies)) await fetchTickmillCompanies()

          const leadsFilter = findFilterById(props.filterId, gridState.filter.filters)
          const value = leadsFilter?.value?.filter((x: LeadDto) => x) || []

          setSelectedLeads(value)
          if (response?.items) {
            setLeads(
              response.items.map((lead: LeadDto) => {
                if (value.length <= 0) {
                  return { ...lead, selected: false }
                }

                const match = value?.find((x: LeadDto) => x.id === lead.id)
                const isSelected = !!match

                return { ...lead, selected: isSelected }
              })
            )
          }

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<LeadDto[]>>
  ) => {
    setLoading(true)

    const newGridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback(newGridState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      setGridFilterState({
        ...newGridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(normalizeLeadsSearchQuery(filter ?? gridState.filter)) ?? null
    },
    [gridState.filter]
  )

  const filterFetch = useCallback(
    async (params?: {
      query?: string | null
      filter?: CompositeFilterDescriptor
    }): Promise<void> => {
      const searchQuery =
        params?.query !== undefined ? params.query : getQuery(gridFilterState.filter)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await leadsApiClient.getLeads({
        search: searchQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      if (response?.items) {
        setLeads(response.items.map((dataItem: LeadDto) => ({ ...dataItem, selected: false })))
      }
      setGridFilterState({
        ...gridFilterState,
        filter: params?.filter ?? gridFilterState.filter,
        take,
        skip,
        total,
        pageIndex,
      })
    },
    [gridFilterState, gridState.pageSize, gridState.take, getQuery] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const selectLeads = (leadItems: LeadDto[]) =>
    leadItems.map((lead: LeadDto) => {
      const match = selectedLeads.find((x) => x.id === lead.id)
      const isSelected = !!match
      return { ...lead, selected: isSelected }
    })

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })

    const searchQuery = getQuery(gridFilterState.filter)
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
    })

    const response = await leadsApiClient.getLeads({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    const total = response.totalResults

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
      total,
    })
    if (response?.items) {
      setLeads(selectLeads(response.items))
    }
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setLeads(leads.map((lead) => ({ ...lead, selected: isChecked })))

    const leadsSelected = isChecked
      ? uniqBy([...leads, ...selectedLeads], 'id')
      : selectedLeads.filter((selectedLead) => {
          const hasLeads = leads.find((x) => x.id === selectedLead.id)

          return !hasLeads
        })

    setSelectedLeads(leadsSelected)
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem.selected) {
      const lead = leads.find((x) => x.id === event.dataItem.id)
      if (lead) {
        setSelectedLeads([...selectedLeads, lead])
      }
    } else {
      setSelectedLeads(selectedLeads.filter((x) => x.id !== event.dataItem.id))
    }

    setLeads(
      leads.map((item) => {
        if (item.id === event.dataItem.id) {
          return { ...item, selected: !event.dataItem.selected }
        }
        return item
      })
    )
  }

  const handleSubmitLeads = () => {
    props.onSelect?.({
      filterId: props.filterId,
      value: uniqBy(selectedLeads, 'id'),
    })
    props.onClose()
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <DialogContent size='3xl'>
      <DialogHeader>
        <DialogTitle>Select Lead</DialogTitle>
        <SelectFiltersFacade
          gridState={gridFilterState}
          setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
          filterFetch={filterFetch}
          filterType={FilterType.LEADS}
          filterFields={getLeadFields()}
          containerClassName='lead-table__filter'
          getQuery={getQuery}
        />
      </DialogHeader>
      <Grid
        {...gridFilterState}
        resizable
        data={leads}
        selectedField='selected'
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={handleGridPageChange}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        {props.singleLead ? (
          <Column
            field='Select'
            width={130}
            cell={(gridCellProps: GridCellProps) => (
              <td data-test='select-column'>
                <Button
                  onMouseDown={() => {
                    props.onSelectSingle && props.onSelectSingle(gridCellProps.dataItem)
                    props.onClose()
                  }}
                  data-test='select-button'
                >
                  Select
                </Button>
              </td>
            )}
          />
        ) : (
          <Column
            field='selected'
            width='50px'
            headerSelectionValue={leads.findIndex((dataItem) => !dataItem.selected) === -1}
          />
        )}
        {leadsColumns
          .filter((c) => c.show)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
      </Grid>
      {!props.singleLead && (
        <DialogFooter>
          <Button onClick={handleSubmitLeads}>Save</Button>
        </DialogFooter>
      )}
    </DialogContent>
  )
}

const findFilterById = (
  filterId?: string,
  filters?: (FilterDescriptor | CompositeFilterDescriptor)[]
) => {
  return (
    (filters?.find((filter: FilterDescriptor | CompositeFilterDescriptor) => {
      if (instanceOfFilterDescriptor(filter)) {
        return filter.id === filterId
      }
      return false
    }) as FilterDescriptor) ?? []
  )
}

export default SelectLeadTable
